import React, { useCallback, useEffect, useState } from "react";
import { Link2, Copy, Check, Loader2, ShieldCheck, Pencil, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../api";
import { startCheckout } from "../../utils/checkout";
import { useLang } from "../../context/LanguageContext";
import { useToast } from "../../hooks/use-toast";

// One link per verified shop, built from its own name and on by default. Verification is what
// stops someone trading under a name they don't own, so the link waits for it.
export const ShopLinkCard = () => {
  const { t } = useLang();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [slug, setSlug] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/my/shop-link").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  useEffect(load, [load]);

  if (!data) return null;

  const copy = async (url, which) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(which); setTimeout(() => setCopied(""), 1800);
      toast({ title: t("sl.copied"), description: t("sl.copiedNote") });
    } catch { toast({ title: url }); }
  };

  const toggle = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/my/shop-link", { on: !data.on });
      toast({ title: res.on ? t("sl.switchedOn") : t("sl.switchedOff") });
      load();
    } catch (e) { toast({ title: e.response?.data?.detail || t("prc.tryAgain") }); }
    finally { setBusy(false); }
  };

  if (!data.verified) {
    return (
      <div className="bg-card border border-border rounded-2xl p-5" data-testid="shop-link-card">
        <h3 className="font-bold text-foreground flex items-center gap-2"><Link2 size={17} className="text-[#16a34a]" /> {t("sl.title")}</h3>
        <p className="text-sm text-muted-foreground mt-1.5" data-testid="shop-link-locked">{t("sl.needVerified")}</p>
        <Link to="/account#verify" data-testid="shop-link-verify"
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] text-white rounded-xl px-4 py-2.5 transition-colors">
          <ShieldCheck size={13} /> {t("sl.getVerified")}
        </Link>
      </div>
    );
  }

  const msg = t("sl.shareMsg", { n: data.liveDeals });
  const price = `$${Number(data.customPrice || 9).toFixed(0)}`;

  const saveSlug = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.post("/my/shop-link/slug", { slug });
      toast({ title: t("sl.customSaved"), description: `${data.linkBase || "/s/"}${res.slug}` });
      setEditing(false);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast({ title: typeof d === "string" ? d : d?.message || t("prc.tryAgain") });
    } finally { setSaving(false); }
  };

  const buyCustom = async () => {
    setSaving(true);
    try { await startCheckout("custom_link"); }
    catch (e) {
      const d = e.response?.data?.detail;
      toast({ title: typeof d === "string" ? d : t("prc.tryAgain") });
      setSaving(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="shop-link-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-bold text-foreground flex items-center gap-2"><Link2 size={17} className="text-[#16a34a]" /> {t("sl.title")}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{t("sl.sub", { n: data.liveDeals })}</p>
        </div>
        <button onClick={toggle} disabled={busy} role="switch" aria-checked={data.on} data-testid="shop-link-toggle"
          className={`inline-flex items-center gap-2 text-xs font-bold rounded-full pl-1 pr-3 py-1 transition-colors disabled:opacity-60 ${data.on ? "bg-[#16a34a] text-white" : "bg-secondary text-muted-foreground"}`}>
          <span className={`h-5 w-5 rounded-full bg-white flex items-center justify-center transition-transform ${data.on ? "translate-x-0" : ""}`}>
            {busy ? <Loader2 className="animate-spin text-[#16a34a]" size={11} /> : data.on ? <Check size={12} className="text-[#16a34a]" /> : <span className="h-2 w-2 rounded-full bg-muted-foreground" />}
          </span>
          {data.on ? t("sl.on") : t("sl.off")}
        </button>
      </div>

      {data.on ? (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <code className="flex-1 min-w-[240px] bg-secondary rounded-xl px-3 py-2.5 text-sm font-semibold text-foreground truncate" data-testid="shop-link-value">{data.prettyUrl}</code>
            <button onClick={() => copy(data.shareUrl, "shop")} data-testid="shop-link-copy"
              className="text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] text-white rounded-xl px-4 py-2.5 transition-colors flex items-center gap-1.5">
              {copied === "shop" ? <Check size={13} /> : <Copy size={13} />} {t("sl.copy")}
            </button>
            <a href={`https://wa.me/?text=${encodeURIComponent(msg + " " + data.shareUrl)}`} target="_blank" rel="noreferrer"
              data-testid="shop-link-whatsapp"
              className="text-xs font-bold text-white rounded-xl px-4 py-2.5 transition-opacity hover:opacity-90" style={{ background: "#25D366" }}>
              WhatsApp
            </a>
          </div>
          {data.exampleDeal && (
            <div className="rounded-xl bg-secondary/60 p-3" data-testid="shop-link-deal">
              <p className="text-[11px] font-semibold text-foreground">{t("sl.dealLinks", { title: data.exampleDeal.title })}</p>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <code className="flex-1 min-w-[220px] text-xs text-muted-foreground truncate" data-testid="shop-link-deal-url">{data.exampleDeal.prettyUrl}</code>
                <button onClick={() => copy(data.exampleDeal.shareUrl, "deal")} data-testid="shop-link-deal-copy"
                  className="text-[11px] font-bold border border-border hover:bg-secondary rounded-lg px-3 py-1.5 transition-colors flex items-center gap-1.5">
                  {copied === "deal" ? <Check size={11} className="text-[#16a34a]" /> : <Copy size={11} />} {t("sl.copy")}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground mt-3" data-testid="shop-link-off-note">{t("sl.offNote")}</p>
      )}

      <div className="mt-4 pt-4 border-t border-border" data-testid="shop-link-custom">
        <p className="text-sm font-bold text-foreground flex items-center gap-1.5">
          <Pencil size={14} className="text-[#16a34a]" /> {t("sl.customTitle")}
        </p>
        {data.canCustom ? (
          <>
            <p className="text-xs text-muted-foreground mt-1">
              {data.plusPerk ? t("sl.customPlus") : t("sl.customOwned")}
            </p>
            {editing ? (
              <div className="mt-2.5 flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground shrink-0">{data.linkBase}</span>
                <input value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="shop-link-slug-input"
                  placeholder={t("sl.customPlaceholder")} maxLength={40}
                  className="flex-1 min-w-[160px] bg-secondary rounded-xl px-3 py-2.5 text-sm font-semibold text-foreground outline-none focus:ring-2 ring-[#16a34a]/40" />
                <button onClick={saveSlug} disabled={saving || slug.trim().length < 2} data-testid="shop-link-slug-save"
                  className="text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-50 text-white rounded-xl px-4 py-2.5 transition-colors flex items-center gap-1.5">
                  {saving ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />} {t("sl.customSave")}
                </button>
                <button onClick={() => setEditing(false)} data-testid="shop-link-slug-cancel"
                  className="text-xs font-semibold text-muted-foreground hover:text-foreground px-2 py-2.5">{t("sl.customCancel")}</button>
              </div>
            ) : (
              <button onClick={() => { setSlug(data.slug || ""); setEditing(true); }} data-testid="shop-link-slug-edit"
                className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:bg-secondary rounded-xl px-4 py-2.5 transition-colors">
                <Pencil size={13} /> {t("sl.customEdit")}
              </button>
            )}
          </>
        ) : (
          <>
            <p className="text-xs text-muted-foreground mt-1" data-testid="shop-link-custom-offer">{t("sl.customOffer", { price })}</p>
            <button onClick={buyCustom} disabled={saving} data-testid="buy-custom-link-btn"
              className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white rounded-xl px-4 py-2.5 transition-colors">
              {saving ? <Loader2 className="animate-spin" size={13} /> : <Crown size={13} className="text-[#f5b942]" />}
              {t("sl.customBuy", { price })}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ShopLinkCard;
